import { ToolPanel } from "../components/ToolPanel";
import { useEffect, useState } from "react";
import { Screensaver } from "../components/Screensaver";
import { useIdleTimer } from "../hooks/useIdleTimer";
import { Footer } from "../components/Footer";
import { useAuth, app } from "../contexts/AuthContext";
import typographyImageBase from 'figma:asset/2145e4efd90b06389836e8206ea66f0e40b20e89.png';
import typographyImageBottom from 'figma:asset/9b8d462bb3544bd7ef5f797be7a968ee6c2eda42.png';
import descImage1 from 'figma:asset/7fd55fee59adc5ceaca1c31865f690922447bf76.png';
import descImage2 from 'figma:asset/3ed8b5352fb2066355c1895f695944a1dced2481.png';
import descImage3 from 'figma:asset/8b9fbaef1a71956d0db80a0007c6ccf3f3893e43.png';
import asciiImageBase from 'figma:asset/bba6a9687c6d644288066d1084237b1ececafad3.png';
import asciiImageBottom from 'figma:asset/91aee102210cafb0c5c3f330318b2b5fd924f119.png';
import dotImageBase from 'figma:asset/d00858fed7cf9defbd0e685ee5f86d56bcdc1450.png';
import dotImageBottom from 'figma:asset/7ce6739730ad3f4c57fa92356c5f56358470f9e9.png';

const tools = [
  {
    id: 1,
    title: "字体配对工具",
    description:
      "快速找到完美的字体组合，提升你的设计品质。提供实时预览和智能推荐。",
    image: typographyImageBase,
    imageAlt: typographyImageBottom,
    category: "TYPOGRAPHY",
    link: "/dstudio",
    descriptionImage: descImage1,
  },
  {
    id: 2,
    title: "调色板生成器",
    description:
      "基于色彩理论生成和谐的配色方案，支持导出多种格式，适配各种设计工具。",
    image: dotImageBase,
    imageAlt: dotImageBottom,
    category: "COLOR",
    link: "/halftone",
    descriptionImage: descImage2,
  },
  {
    id: 3,
    title: "网格系统计算器",
    description:
      "精确计算网格尺寸和间距，让你的布局更加专业和规范，支持响应式设计。",
    image: asciiImageBase,
    imageAlt: asciiImageBottom,
    category: "LAYOUT",
    descriptionImage: descImage3,
    link: "/ascii",
  },
  {
    id: 4,
    title: "光栅效果生成器",
    description:
      "基于网版套色与半色调算法，支持分色、抖动与多通道导出，适配印刷与海报风格。",
    image: '/LOGO23_画板 1-02.jpg',
    imageAlt: '/LOGO23_画板 1-01.jpg',
    category: "RASTER",
    link: "/raster/",
    descriptionImage: descImage1,
  },
];

const HIDE_RASTER = true;
const HIDE_DSTUDIO_LABEL = true;

export function Home() {
  // 30秒无操作后进入待机模式
  const { isIdle, resetTimer } = useIdleTimer(30000);
  const [dataTools, setDataTools] = useState(tools);
  const { isLoggedIn, setShowLoginDialog, getUid, user, refreshMembership } = useAuth();

  const handleBuyRaster = async () => {
    try {
      if (!isLoggedIn) {
        setShowLoginDialog(true);
        return;
      }
      const uid = await getUid();
      const res: any = await (app as any).callFunction({
        name: 'createOrder',
        data: { planId: 'raster', uid, email: user?.email || '' }
      });
      const result = res?.result || res;
      if (result?.code === 0 && result?.data?.payUrl) {
        const url = result.data.payUrl;
        try {
          const w = window.open(url, '_blank');
          if (!w) window.location.assign(url);
        } catch {
          window.location.assign(url);
        }
        try {
          let t = 0;
          const timer = setInterval(async () => {
            t++;
            await refreshMembership();
            if (t >= 20) clearInterval(timer);
          }, 2000);
        } catch {}
      } else {
        alert('创建订单失败');
      }
    } catch (e: any) {
      alert(e?.message || '支付服务暂时不可用');
    }
  };

  useEffect(() => {
    if (!(import.meta as any).env?.DEV) return;
    const fixUrl = (u: string) => (u && u.startsWith('/uploads/') ? `http://localhost:4000${u}` : u || '');
    const load = () => {
      fetch('http://localhost:4000/data')
        .then((r) => r.json())
        .then((d) => {
          const arr = Array.isArray(d?.tools) ? d.tools : [];
          if (arr.length) {
            const mapById: Record<number, any> = {};
            for (const r of arr) { if (r && typeof r.id === 'number') mapById[r.id] = r; }
            const merged = tools.map((t) => {
              const r = mapById[t.id];
              if (!r) return t;
              return {
                ...t,
                image: fixUrl(r.image) || t.image,
                imageAlt: fixUrl(r.imageAlt) || t.imageAlt,
                link: r.link ?? t.link,
              };
            });
            setDataTools(merged);
          }
        })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 5000);
    return () => { clearInterval(timer); };
  }, []);

  return (
    <>
      {/* Screensaver */}
      {isIdle && <Screensaver onExit={resetTimer} />}

      {/* Tools Grid - Left/Right Split Layout */}
      <main className="pt-32 px-8 pb-8">
        <div className="max-w-6xl mx-auto px-8 lg:px-0 overflow-visible">
          {/* First Row - 50/50 Split */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="w-full max-w-[460px] lg:ml-12 relative group">
              {/* Description Image - Positioned outside on the left */}
              {dataTools[0].descriptionImage && (
                <div className="absolute top-0 left-[-260px] w-[240px] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <img
                    src={dataTools[0].descriptionImage}
                    alt="Tool description"
                    className="w-full h-auto group-hover:animate-border-blink"
                    style={{ boxShadow: '0 0 0 1px #3b82f6' }}
                  />
                </div>
              )}
              <ToolPanel
                title={dataTools[0].title}
                description={dataTools[0].description}
                image={dataTools[0].image}
                imageAlt={dataTools[0].imageAlt}
                category={dataTools[0].category}
                link={dataTools[0].link}
                inverted={true}
                toolLabel={HIDE_DSTUDIO_LABEL ? undefined : "D · Studio效果应用"}
              />
            </div>
            <div className="lg:translate-x-[calc((100vw-1280px)/2+1280px/2-50%-20rem)] w-full max-w-[460px] relative group">
              {/* Description Image - Positioned outside on the left */}
              {dataTools[1].descriptionImage && (
                <div className="absolute top-0 left-[-260px] w-[240px] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <img
                    src={dataTools[1].descriptionImage}
                    alt="Tool description"
                    className="w-full h-auto group-hover:animate-border-blink"
                    style={{ boxShadow: '0 0 0 1px #3b82f6' }}
                  />
                </div>
              )}
              <ToolPanel
                title={dataTools[1].title}
                description={dataTools[1].description}
                image={dataTools[1].image}
                imageAlt={dataTools[1].imageAlt}
                category={dataTools[1].category}
                link={dataTools[1].link}
                inverted={false}
                toolLabel="点状效果生成工具"
              />
            </div>
          </div>

          {/* Second Row - 50/50 Split */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-8">
            <div className="w-full max-w-[460px] lg:ml-12 relative group">
              {/* Description Image - Positioned outside on the left */}
              {dataTools[2].descriptionImage && (
                <div className="absolute top-0 left-[-260px] w-[240px] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <img
                    src={dataTools[2].descriptionImage}
                    alt="Tool description"
                    className="w-full h-auto group-hover:animate-border-blink"
                    style={{ boxShadow: '0 0 0 1px #3b82f6' }}
                  />
                </div>
              )}
              <ToolPanel
                title={dataTools[2].title}
                description={dataTools[2].description}
                image={dataTools[2].image}
                imageAlt={dataTools[2].imageAlt}
                category={dataTools[2].category}
                link={dataTools[2].link}
                inverted={true}
                toolLabel="ASCII效果生成器"
              />
            </div>
            <div className="lg:translate-x-[calc((100vw-1280px)/2+1280px/2-50%-20rem)] w-full max-w-[460px] relative group">
              {/* Description Image - Positioned outside on the left */}
              {!HIDE_RASTER && dataTools[3].descriptionImage && (
                <div className="absolute top-0 left-[-260px] w-[240px] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <img
                    src={dataTools[3].descriptionImage}
                    alt="Tool description"
                    className="w-full h-auto group-hover:animate-border-blink"
                    style={{ boxShadow: '0 0 0 1px #3b82f6' }}
                  />
                </div>
              )}
              {HIDE_RASTER ? (
                <ToolPanel inverted={false} locked={true} />
              ) : (
                <ToolPanel
                  title={dataTools[3].title}
                  description={dataTools[3].description}
                  image={dataTools[3].image}
                  imageAlt={dataTools[3].imageAlt}
                  category={dataTools[3].category}
                  link={dataTools[3].link}
                  inverted={false}
                  toolLabel="光栅效果生成器"
                  overlayImage={"/VIPpng.png"}
                  requiresVip={true}
                  hoverBadgeImage={'/DADODY-02.png'}
                  hoverSideImage={'/raster-lines-effect-03.png'}
                  hoverSideRight={8}
                  hoverSideBottom={4}
                  unlockIfProduct="raster"
                  onSideButtonClick={handleBuyRaster}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Feedback Button Below Grid */}
      {!HIDE_RASTER && (
        <div className="px-8 pb-8">
          <div className="max-w-6xl mx-auto px-8 lg:px-0">
            <div className="w-full max-w-[460px] lg:ml-12">
              <ToolPanel inverted={false} locked={true} />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}
